import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import { useVoiceCallSession } from '../../../hooks/use-voice-call-session'
import { isNativeApp } from '../../../utils/platform'
import { getDeviceId } from '../../../utils/device-id'
import { isTerminalCallPhase } from '../../../stores/voice-call-store'
import {
  isWebMfaOverdue,
  recordWebMfaUnlock,
  setAppLocked,
  useIsAppLocked,
} from '../../../stores/app-lock-store'
import {
  resendWebMfaChallenge,
  startWebMfaChallenge,
  verifyWebMfaChallenge,
} from '../../../services/appsync-client'
import {
  SOFT_LOCK_VERIFY_COPY,
  requiresWebWeeklyMfa,
} from '../session-security-policy'
import { registerSoftLockUnlock } from '../soft-lock-bridge'

type Step = 'password' | 'otp'

/**
 * Web-only weekly MFA soft-lock: password + app-owned email OTP every 7×24h.
 * Reuses the same locked flag / SoftLockedText / ensureSoftUnlocked gating as
 * native biometric soft-lock. Does not run on native (12h biometric is separate).
 */
export function useWebMfa() {
  const { user, signOut } = useAuth()
  const callSession = useVoiceCallSession()
  const locked = useIsAppLocked()
  const [policyActive, setPolicyActive] = useState(false)
  const [step, setStep] = useState<Step>('password')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [destinationHint, setDestinationHint] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [resendIn, setResendIn] = useState(0)
  const busyRef = useRef(false)
  const passwordRef = useRef('')

  const suppressSheetForCall =
    !!callSession && !isTerminalCallPhase(callSession.phase)

  useEffect(() => {
    passwordRef.current = password
  }, [password])

  useEffect(() => {
    if (isNativeApp() || !user) {
      setPolicyActive(false)
      return
    }
    const eligible = requiresWebWeeklyMfa(user)
    setPolicyActive(eligible)
    if (!eligible) {
      // Members/independents: do not hold a web MFA lock.
      setAppLocked(false)
      return
    }
    setAppLocked(isWebMfaOverdue())
  }, [user?.userId, user?.roleType, user?.providerLevel])

  // Re-check when the tab becomes visible again (separate from native App listener).
  useEffect(() => {
    if (isNativeApp() || !policyActive) return
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && isWebMfaOverdue()) {
        setAppLocked(true)
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [policyActive])

  useEffect(() => {
    if (resendIn <= 0) return
    const t = window.setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000)
    return () => window.clearTimeout(t)
  }, [resendIn])

  const parseError = (err: unknown): string => {
    const raw = err instanceof Error ? err.message : String(err)
    if (/INVALID_PASSWORD/i.test(raw)) return 'Incorrect password. Try again.'
    if (/INVALID_CODE/i.test(raw)) return 'Incorrect code. Try again.'
    if (/EXPIRED/i.test(raw)) return 'Code expired. Request a new one.'
    if (/TOO_MANY_ATTEMPTS/i.test(raw)) return 'Too many attempts. Request a new code.'
    if (/COOLDOWN/i.test(raw)) return 'Please wait a moment before requesting another code.'
    if (/EMAIL_FAILED|sandbox/i.test(raw)) {
      return 'We could not send your verification code email. Try again, or sign out and contact support if it keeps failing.'
    }
    if (/FORBIDDEN/i.test(raw)) return 'Weekly verification is not required for this account.'
    return 'Something went wrong. Try again.'
  }

  const submitPassword = useCallback(async (): Promise<boolean> => {
    if (busyRef.current) return false
    const pwd = passwordRef.current
    if (!pwd) {
      setErrorMessage('Enter your password to continue.')
      return false
    }
    busyRef.current = true
    setBusy(true)
    setErrorMessage(null)
    try {
      const result = await startWebMfaChallenge({
        deviceId: getDeviceId(),
        password: pwd,
      })
      setDestinationHint(result.destinationHint)
      setResendIn(result.resendAvailableInSeconds ?? 60)
      setStep('otp')
      setCode('')
      return true
    } catch (err) {
      setErrorMessage(parseError(err))
      return false
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [])

  const submitCode = useCallback(async (): Promise<boolean> => {
    if (busyRef.current) return false
    const trimmed = code.replace(/\s+/g, '')
    if (trimmed.length < 6) {
      setErrorMessage('Enter the 6-digit code from your email.')
      return false
    }
    busyRef.current = true
    setBusy(true)
    setErrorMessage(null)
    try {
      await verifyWebMfaChallenge({
        deviceId: getDeviceId(),
        code: trimmed,
      })
      recordWebMfaUnlock()
      setPassword('')
      setCode('')
      setStep('password')
      setDestinationHint(null)
      return true
    } catch (err) {
      setErrorMessage(parseError(err))
      return false
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [code])

  const resend = useCallback(async () => {
    if (busyRef.current || resendIn > 0) return
    const pwd = passwordRef.current
    if (!pwd) {
      setStep('password')
      setErrorMessage('Re-enter your password to resend the code.')
      return
    }
    busyRef.current = true
    setBusy(true)
    setErrorMessage(null)
    try {
      const result = await resendWebMfaChallenge({
        deviceId: getDeviceId(),
        password: pwd,
      })
      setDestinationHint(result.destinationHint)
      setResendIn(result.resendAvailableInSeconds ?? 60)
    } catch (err) {
      setErrorMessage(parseError(err))
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [resendIn])

  const attemptUnlock = useCallback(async (): Promise<boolean> => {
    // Imperative bridge: if already on OTP and code filled, try verify; else focus password step.
    if (step === 'otp' && code.replace(/\s+/g, '').length >= 6) {
      return submitCode()
    }
    if (step === 'password' && passwordRef.current) {
      return submitPassword()
    }
    // Show the sheet; caller waits for user interaction via UI buttons.
    setAppLocked(true)
    return false
  }, [step, code, submitCode, submitPassword])

  useEffect(() => {
    if (isNativeApp()) return
    registerSoftLockUnlock(attemptUnlock)
    return () => registerSoftLockUnlock(null)
  }, [attemptUnlock])

  // Fail-closed sign-out from the MFA sheet. The screen fires it as
  // `void signOut()`, so a rejection would vanish — catch here and surface it
  // on the sheet. On failure the web MFA clock is untouched (auth-context
  // clears it only after a confirmed teardown), so the user stays locked.
  const signOutFromSheet = useCallback(async () => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setErrorMessage(null)
    try {
      await signOut()
    } catch {
      setErrorMessage('Sign-out failed. Try again.')
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [signOut])

  const showSheet =
    !isNativeApp() &&
    Boolean(user) &&
    policyActive &&
    locked &&
    !suppressSheetForCall

  return {
    locked: !isNativeApp() && Boolean(user) && policyActive && locked,
    showSheet,
    step,
    password,
    setPassword,
    code,
    setCode,
    destinationHint,
    busy,
    errorMessage,
    resendIn,
    verifyCopy: SOFT_LOCK_VERIFY_COPY,
    submitPassword,
    submitCode,
    resend,
    backToPassword: () => {
      setStep('password')
      setCode('')
      setErrorMessage(null)
    },
    signOut: signOutFromSheet,
  }
}
